import { Box, Divider, Stack } from '@mui/material';
import { BACK_OFFICE } from 'business/constants';
import type { StatusesByField } from 'business/types';
import FieldStatusDescription from 'components/SharedComponents/FieldStatusInformation/FieldStatusDescription';
import { useIsExploreUser } from 'hooks/client';
import { useAPIPutDataCatalogField } from 'modules/datacatalog/api/useAPIPutDataCatalogFields';
import type {
    CatalogSelection,
    DataCatalogField,
    FieldStatusData,
} from 'modules/datacatalog/types';
import { useAPIGetSuggestedCharts } from 'modules/decision/api/chart/useAPIGetSuggestedCharts';
import SingleIndicatorsBanner from 'modules/decision/components/SingleIndicatorsBanner/SingleIndicatorsBanner';
import { useTranslation } from 'react-i18next';
import { CSV } from 'routes/Support/components/ClientSupport/components/ClientConfiguration/ModuleForm/helpers/definitions';
import { useSelector } from 'store/hooks';

import DataPreview from './DataPreview/DataPreview';
import FieldDefinition from './FieldDefinition';
import FieldFormula from './FieldFormula';
import FieldHeader from './FieldHeader';
import FieldTips from './FieldTips';
import TableFields from './TableFields';

type Props = {
    selection: CatalogSelection;
    catalogMetadata: DataCatalogField;
    displayedStatuses?: StatusesByField;
};
const FieldDetail = ({
    selection,
    catalogMetadata,
    displayedStatuses,
}: Props) => {
    const isExploreUser = useIsExploreUser();
    const { t } = useTranslation();
    const { item, domain, category } = selection;
    const { isSupportUser, modules } = useSelector((s) => ({
        isSupportUser: s.vision.isSupportUser,
        modules: s.vision.client.modules,
    }));
    const canEdit = isSupportUser && modules.includes(BACK_OFFICE);
    const { mutate } = useAPIPutDataCatalogField();
    const fieldDescription =
        catalogMetadata.description ||
        (domain === CSV ? t('dataCatalog.csvFilesNotSupported') : undefined);

    const {
        lifeCycleStatus,
        statusAffectedSoftware,
        statusDescription,
        statusEndDate,
    } = catalogMetadata;
    const fieldStatusData: FieldStatusData = {
        lifeCycleStatus,
        statusAffectedSoftware,
        statusDescription,
        statusEndDate,
    };
    return (
        <Stack spacing={1}>
            <FieldHeader
                item={item}
                domain={domain}
                category={category}
                isFieldVisible={catalogMetadata.isVisible}
                mappingCategory={catalogMetadata.mappingCategory}
                canEdit={canEdit}
                fieldStatusData={fieldStatusData}
                displayedStatuses={displayedStatuses}
            />
            <Divider />
            <Stack spacing={2}>
                {displayedStatuses && (
                    <FieldStatusDescription
                        currentStatus={displayedStatuses[item.field]}
                        content={statusDescription}
                    />
                )}
                <FieldDefinition
                    canEdit={canEdit}
                    description={fieldDescription}
                    onSave={(description) =>
                        mutate({ fieldName: item.field, description })
                    }
                />
                {selection.kind === 'table' && (
                    <TableFields fields={selection.fields} />
                )}
                {selection.kind === 'field' && (
                    <FieldFormula
                        canEdit={canEdit}
                        formula={catalogMetadata.formula}
                        onSave={(formula) =>
                            mutate({ fieldName: item.field, formula })
                        }
                    />
                )}
                <DataPreview selection={selection} />
                <FieldTips
                    canEdit={canEdit}
                    tips={catalogMetadata.tips}
                    onSave={(tips) =>
                        mutate({
                            fieldName: item.field,
                            tips,
                        })
                    }
                />
                {isExploreUser && (
                    <Box sx={{ mt: 4 }}>
                        <SingleIndicatorsBanner
                            displayMode="catalog"
                            metricsFilter={[item.field]}
                            dimensionsFilter={[item.field]}
                            filtersFilter={[item.field]}
                            searchMode="or"
                            useGetCharts={useAPIGetSuggestedCharts}
                        />
                    </Box>
                )}
            </Stack>
        </Stack>
    );
};

export default FieldDetail;
